// statssaIndices.js
// ─────────────────────────────────────────────────────────────────────────────
// Client helper for the CPA "Auto-fill from StatsSA" button. It parses the entry's
// index month, calls the secured fetchStatssaIndices Cloud Function (which fetches
// the StatsSA PDFs and reads the index numbers out of them — see
// functions/statssa.js), and maps the result onto the CPA table's index labels.
//
// Best-effort by design: StatsSA releases late and shifts layouts, so any series
// can come back null. The caller shows the values as provisional for the engineer
// to verify before they feed a payment certificate — never trusted blind.
// ─────────────────────────────────────────────────────────────────────────────

import { getFunctions, httpsCallable } from "firebase/functions";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// "May 2024" / "2024-05" → { monthName, year, ym } | null
export function parseIndexMonth(input) {
  if (!input) return null;
  const s = String(input).trim();

  const iso = s.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (iso) {
    const mi = parseInt(iso[2], 10) - 1;
    if (mi < 0 || mi > 11) return null;
    return { monthName: MONTHS[mi], year: iso[1], ym: `${iso[1]}-${String(mi + 1).padStart(2, "0")}` };
  }

  const m = s.match(/^([A-Za-z]{3,})\.?\s+(\d{4})$/);
  if (m) {
    const frag = m[1].toLowerCase().slice(0, 3);
    const idx = MONTHS.findIndex((mn) => mn.toLowerCase().startsWith(frag));
    if (idx < 0) return null;
    return { monthName: MONTHS[idx], year: m[2], ym: `${m[2]}-${String(idx + 1).padStart(2, "0")}` };
  }
  return null;
}

// Which canonical StatsSA series maps onto which CPA index labels. Only these
// four labels (the default CPA table) can be auto-filled; other tables' custom
// labels are left for manual entry.
export const STATSSA_SERIES_KEYS = ["labourIndex", "plantIndex", "materialIndex", "fuelIndex"];

// Human labels for messaging.
export const STATSSA_SERIES_LABELS = {
  labourIndex: "Labour (P0141 CPI headline)",
  plantIndex: "Plant (P0151.1 Table 4)",
  materialIndex: "Material (P0151.1 Table 6)",
  fuelIndex: "Fuel (P0142.1 diesel)",
};

// P0151.1 Table 6 rows a project can escalate materials on. Keys mirror
// functions/statssa.js MATERIAL_TABLE6_ROWS (the backend extracts ALL rows per
// month; the project setting picks which one feeds `materialIndex`).
export const MATERIAL_SERIES_OPTIONS = [
  { key: "total", label: "Civil engineering material — total" },
  { key: "ex_bitumen", label: "Civil engineering material (excl. bitumen)" },
  { key: "ex_reinforcing", label: "Civil engineering material (excl. reinforcing metal work)" },
  { key: "ex_reinforcing_bitumen", label: "Civil engineering material (excl. reinforcing & bitumen)" },
  { key: "structures_ex_bitumen", label: "Structures (excl. bitumen)" },
  { key: "roads_general", label: "Roads, general" },
  { key: "roads_general_ex_bitumen", label: "Roads, general (excl. bitumen) — default" },
  { key: "roads_refurbishment", label: "Roads, refurbishment" },
  { key: "roads_refurbishment_ex_bitumen", label: "Roads, refurbishment (excl. bitumen)" },
  { key: "roads_reseal", label: "Roads, reseal" },
  { key: "roads_reseal_ex_bitumen", label: "Roads, reseal (excl. bitumen)" },
  { key: "bulk_earthworks", label: "Bulk earthworks" },
  { key: "bulk_earthworks_ex_bitumen", label: "Bulk earthworks (excl. bitumen)" },
];
export const MATERIAL_SERIES_DEFAULT_KEY = "roads_general_ex_bitumen";

// Call the Cloud Function for a given index month. Returns the raw result object
// { ym, series, sources, publications, status, missing, errors, cached, fetchedAt }.
export async function fetchStatssaIndices({ ym, monthName, year, force = false }) {
  const fn = httpsCallable(getFunctions(), "fetchStatssaIndices");
  const res = await fn({ ym, month: monthName, year, force });
  return res.data || {};
}

// Given a CPA table's rowIndexLabels and a StatsSA result, produce the patch of
// index values to merge into the form, plus which labels were filled / skipped.
export function mapResultToIndexValues(rowIndexLabels, result, options = {}) {
  const series = { ...((result && result.series) || {}) };
  const provisionalSrc = (result && result.provisional) || {};
  // Project-selected Table 6 row overrides the default material series when the
  // month's extraction carried the full table.
  const materialKey = options.materialSeriesKey;
  const table6 = (result && result.materialTable6) || {};
  if (materialKey && table6[materialKey] != null) {
    series.materialIndex = table6[materialKey];
  }
  const filled = [];
  const patch = {};
  // Per-filled-label provisional flag: true = value was carried from an earlier
  // month (StatsSA hadn't published the requested month), so the UI marks it amber.
  const provisional = {};
  for (const lbl of rowIndexLabels || []) {
    if (STATSSA_SERIES_KEYS.includes(lbl) && series[lbl] != null) {
      patch[lbl] = String(series[lbl]);
      provisional[lbl] = provisionalSrc[lbl] === true;
      filled.push(lbl);
    }
  }
  // Labels this table has that we *could* source but StatsSA didn't return.
  const missing = (rowIndexLabels || []).filter(
    (lbl) => STATSSA_SERIES_KEYS.includes(lbl) && series[lbl] == null
  );
  // Labels this table has that StatsSA can't source at all (custom FIDIC labels).
  const unsupported = (rowIndexLabels || []).filter((lbl) => !STATSSA_SERIES_KEYS.includes(lbl));
  return { patch, filled, missing, unsupported, provisional };
}
